import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  CardBody,
  Badge,
  Code,
  Tooltip,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';

function Mayan() {
  const [txHash, setTxHash] = useState('');
  const [swapData, setSwapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const fetchSwapData = async () => {
    if (!txHash.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a transaction hash',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, search for the transaction hash
      const searchResponse = await fetch(`https://explorer-api.mayan.finance/v3/search?q=${encodeURIComponent(txHash)}`);
      
      if (!searchResponse.ok) {
        throw new Error(`HTTP error! status: ${searchResponse.status}`);
      }

      const searchData = await searchResponse.json();
      
      if (!searchData || searchData.length === 0) {
        throw new Error('No swap found for this transaction hash');
      }

      // Get the order ID from the search result
      const orderId = searchData[0].orderId;
      
      if (!orderId) {
        throw new Error('No order ID found for this transaction');
      }

      // Fetch the detailed swap data using the order ID
      const swapResponse = await fetch(`https://explorer-api.mayan.finance/v3/swap/order-id/${orderId}`);
      
      if (!swapResponse.ok) {
        throw new Error(`HTTP error! status: ${swapResponse.status}`);
      }

      const swapData = await swapResponse.json();
      setSwapData(swapData);
      
      toast({
        title: 'Success',
        description: 'Swap data retrieved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      setError(err.message);
      setSwapData(null);
      toast({
        title: 'Error',
        description: `Failed to fetch swap data: ${err.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    return parseFloat(amount).toFixed(6);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ORDER_UNLOCKED':
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
        return 'yellow';
      case 'FAILED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getChainName = (chainId) => {
    switch (chainId) {
      case '1':
        return 'Solana';
      case '2':
        return 'Ethereum';
      case '3':
        return 'BSC';
      case '4':
        return 'Polygon';
      case '5':
        return 'Avalanche';
      case '6':
        return 'Fantom';
      case '7':
        return 'Arbitrum';
      case '8':
        return 'Optimism';
      case '9':
        return 'Base';
      default:
        return `Chain ${chainId}`;
    }
  };


  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied!',
        description: `${label} copied to clipboard`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="full" px={6} py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="lg" color="gray.900" fontWeight="bold" mb={2}>
              Mayan Swap Explorer
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Track cross-chain swap transactions and bridge operations
            </Text>
          </Box>

          {/* Search Form */}
          <Card bg="white" borderRadius="md" border="1px" borderColor="gray.200">
            <CardBody p={6}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">
                    Transaction Hash
                  </FormLabel>
                  <HStack spacing={3}>
                    <Input
                      placeholder="Enter transaction hash"
                      value={txHash}
                      onChange={(e) => setTxHash(e.target.value)}
                      size="md"
                      bg="white"
                      borderColor="gray.300"
                      color="gray.900"
                      _focus={{ borderColor: "orange.500" }}
                      _placeholder={{ color: "gray.500" }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          fetchSwapData();
                        }
                      }}
                    />
                    <Button
                      colorScheme="orange"
                      size="md"
                      onClick={fetchSwapData}
                      isLoading={loading}
                      loadingText="Searching..."
                      leftIcon={<FiSearch />}
                      isDisabled={!txHash.trim()}
                    >
                      Search
                    </Button>
                  </HStack>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Error State */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="md" color="red.600">Error loading swap data!</AlertTitle>
                <AlertDescription fontSize="sm" color="red.600">{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box textAlign="center" py={8}>
              <Spinner size="lg" color="orange.500" />
              <Text mt={4} color="gray.600">Fetching swap data...</Text>
            </Box>
          )}

          {/* Swap Data */}
          {swapData && !loading && (
            <Box>
              <HStack spacing={2} mb={4}>
                <Heading size="md" color="gray.800" fontWeight="semibold">
                  Swap Details
                </Heading>
                <Badge colorScheme="orange" variant="subtle" fontSize="xs">
                  {swapData.status}
                </Badge>
              </HStack>
              
              <Box bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Created
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Source Address
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Destination Address
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          From Chain
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          To Chain
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3} isNumeric>
                          Amount
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Status
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr _hover={{ bg: 'gray.50' }}>
                        <Td py={3}>
                          <Text fontSize="xs" color="gray.600">
                            {formatDate(swapData.initiatedAt)}
                          </Text>
                        </Td>
                        <Td py={3}>
                          <Tooltip label={`Click to copy: ${swapData.trader || 'N/A'}`} placement="top">
                            <Code 
                              fontSize="xs" 
                              colorScheme="blue" 
                              p={2} 
                              borderRadius="sm" 
                              cursor="pointer"
                              color="blue.800"
                              bg="blue.50"
                              maxW="200px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              onClick={() => swapData.trader && copyToClipboard(swapData.trader, 'Source address')}
                              _hover={{ bg: 'blue.100' }}
                            >
                              {swapData.trader ? `${swapData.trader.slice(0, 8)}...${swapData.trader.slice(-8)}` : 'N/A'}
                            </Code>
                          </Tooltip>
                        </Td>
                        <Td py={3}>
                          <Tooltip label={`Click to copy: ${swapData.destAddress || 'N/A'}`} placement="top">
                            <Code 
                              fontSize="xs" 
                              colorScheme="teal" 
                              p={2} 
                              borderRadius="sm" 
                              cursor="pointer"
                              color="teal.800"
                              bg="teal.50"
                              maxW="200px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              onClick={() => swapData.destAddress && copyToClipboard(swapData.destAddress, 'Destination address')}
                              _hover={{ bg: 'teal.100' }}
                            >
                              {swapData.destAddress ? `${swapData.destAddress.slice(0, 8)}...${swapData.destAddress.slice(-8)}` : 'N/A'}
                            </Code>
                          </Tooltip>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                            {getChainName(swapData.sourceChain)}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                            {getChainName(swapData.destChain)}
                          </Badge>
                        </Td>
                        <Td isNumeric py={3}>
                          <VStack spacing={1} align="end">
                            <Text fontWeight="medium" color="gray.900" fontSize="sm">
                              {formatAmount(swapData.fromAmount)} {swapData.fromTokenSymbol}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              → {formatAmount(swapData.toAmount)} {swapData.toTokenSymbol}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={3}>
                          <Badge 
                            colorScheme={getStatusColor(swapData.status)} 
                            variant="subtle" 
                            fontSize="xs"
                          >
                            {swapData.status}
                          </Badge>
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}

          {/* No Data State */}
          {!swapData && !loading && !error && txHash && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="md">
                No swap data found for this transaction hash
              </Text>
            </Box>
          )}

          {/* Instructions */}
          {!txHash && (
            <Card bg="white" borderRadius="md" border="1px" borderColor="gray.200">
              <CardBody p={6}>
                <VStack spacing={4} align="start">
                  <Heading size="md" color="gray.800" fontWeight="semibold">
                    How to use Mayan Swap Explorer
                  </Heading>
                  <VStack spacing={3} align="start">
                    <Text fontSize="sm" color="gray.600">
                      1. Enter a transaction hash from any supported blockchain
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      2. Click "Search" to fetch swap details and cross-chain operations
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      3. View transaction details and click on transaction links to explore on block explorers
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      4. Track the complete swap flow across multiple chains
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
}

export default Mayan;
